import { call, put, takeEvery, takeLatest, all } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import {
    learningProgressService,
    LearningProgressStats,
    TodayScheduleData,
    DailySchedule,
} from '@/services/learningProgress';

// Learning Progress Actions
import {
    fetchDashboardStats,
    fetchDashboardStatsSuccess,
    fetchDashboardStatsFailure,
    fetchCurrentWeekData,
    fetchCurrentWeekDataSuccess,
    fetchCurrentWeekDataFailure,
    completeQuestion,
    completeQuestionSuccess,
    completeQuestionFailure,
    updateLessonProgress,
    updateLessonProgressSuccess,
    updateLessonProgressFailure,
} from '@/store/slices/learningProgress/learningProgressSlice';

// Weekly Plan Actions
import {
    fetchWeeklyPlan,
    fetchWeeklyPlanSuccess,
    fetchWeeklyPlanFailure,
} from '@/store/slices/learningProgress/weeklyPlanSlice';

// Daily Schedule Actions
import {
    fetchTodaySchedule,
    fetchTodayScheduleSuccess,
    fetchTodayScheduleFailure,
    fetchAvailableSchedules,
    fetchAvailableSchedulesSuccess,
    fetchAvailableSchedulesFailure,
    fetchScheduleWithSubtopics,
    fetchScheduleWithSubtopicsSuccess,
    fetchScheduleWithSubtopicsFailure,
} from '@/store/slices/learningProgress/dailyScheduleSlice';

// Worker Sagas
function* fetchDashboardStatsSaga(action: PayloadAction<{ userId?: number }>) {
    try {
        const response: LearningProgressStats = yield call(learningProgressService.getDashboardStats);
        yield put(fetchDashboardStatsSuccess(response));
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch dashboard stats';
        yield put(fetchDashboardStatsFailure(errorMessage));
    }
}

function* fetchCurrentWeekDataSaga() {
    try {
        const response: any = yield call(learningProgressService.getCurrentWeekData);
        if (response) {
            yield put(fetchCurrentWeekDataSuccess(response));
        } else {
            yield put(fetchCurrentWeekDataFailure('No current week data available'));
        }
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch current week data';
        yield put(fetchCurrentWeekDataFailure(errorMessage));
    }
}

function* completeQuestionSaga(
    action: PayloadAction<{ questionMappingId: string; userAnswer: string; timeSpentMinutes: number }>,
) {
    try {
        const { questionMappingId, userAnswer, timeSpentMinutes } = action.payload;
        yield call(learningProgressService.completeQuestion, questionMappingId, userAnswer, timeSpentMinutes);
        yield put(completeQuestionSuccess({ message: 'Question completed successfully' }));

        // Refresh dashboard stats after completing question
        yield put(fetchDashboardStats({}));
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to complete question';
        yield put(completeQuestionFailure(errorMessage));
    }
}

function* updateLessonProgressSaga(
    action: PayloadAction<{
        scheduleId: string;
        progressData: any;
    }>,
) {
    try {
        const { scheduleId, progressData } = action.payload;
        yield call(learningProgressService.updateLessonProgress, scheduleId, progressData);
        yield put(updateLessonProgressSuccess({ message: 'Lesson progress updated successfully' }));

        // Refresh related data
        yield all([put(fetchDashboardStats({})), put(fetchCurrentWeekData())]);
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update lesson progress';
        yield put(updateLessonProgressFailure(errorMessage));
    }
}

function* fetchWeeklyPlanSaga(action: PayloadAction<{ weekNumber: number }>) {
    try {
        // Get weekly plan from current week data
        const currentWeekData: any = yield call(learningProgressService.getCurrentWeekData);
        if (currentWeekData && currentWeekData.week_number === action.payload.weekNumber) {
            yield put(fetchWeeklyPlanSuccess(currentWeekData));
        } else {
            yield put(fetchWeeklyPlanFailure('Weekly plan not found'));
        }
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch weekly plan';
        yield put(fetchWeeklyPlanFailure(errorMessage));
    }
}

function* fetchTodayScheduleSaga() {
    try {
        const response: TodayScheduleData | null = yield call(learningProgressService.getTodaySchedule);
        if (response) {
            yield put(fetchTodayScheduleSuccess(response));
        } else {
            yield put(fetchTodayScheduleFailure('No schedule available for today'));
        }
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch today schedule';
        yield put(fetchTodayScheduleFailure(errorMessage));
    }
}

function* fetchAvailableSchedulesSaga() {
    try {
        const response: DailySchedule[] = yield call(learningProgressService.getAvailableSchedules);
        yield put(fetchAvailableSchedulesSuccess(response));
    } catch (error: any) {
        console.error(error)
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to fetch available schedules';
        yield put(fetchAvailableSchedulesFailure(errorMessage));
    }
}

function* fetchScheduleWithSubtopicsSaga(action: PayloadAction<{ scheduleId: string }>) {
    try {
        const response: any[] = yield call(
            learningProgressService.getDailyScheduleWithSubtopics,
            action.payload.scheduleId,
        );
        //@ts-expect-error - TODO: fix this
        yield put(fetchScheduleWithSubtopicsSuccess(response));
    } catch (error: any) {
        console.error(error)
        const errorMessage =
            error?.response?.data?.message || error?.message || 'Failed to fetch schedule with subtopics';
        yield put(fetchScheduleWithSubtopicsFailure(errorMessage));
    }
}

// Root Learning Progress Saga
export default function* learningProgressSaga() {
    yield all([
        yield takeLatest(fetchDashboardStats.type, fetchDashboardStatsSaga),
        yield takeLatest(fetchCurrentWeekData.type, fetchCurrentWeekDataSaga),
        yield takeEvery(completeQuestion.type, completeQuestionSaga),
        yield takeEvery(updateLessonProgress.type, updateLessonProgressSaga),
        yield takeLatest(fetchWeeklyPlan.type, fetchWeeklyPlanSaga),
        yield takeLatest(fetchTodaySchedule.type, fetchTodayScheduleSaga),
        yield takeLatest(fetchAvailableSchedules.type, fetchAvailableSchedulesSaga),
        yield takeLatest(fetchScheduleWithSubtopics.type, fetchScheduleWithSubtopicsSaga),
    ]);
}
